import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { FETCH_RECEIPTS_V2, fetchReceiptsV2 } from '~/actions';
import { TSortValue } from '~/commonComponents';
import { shouldUseDsoStructure } from '~/growthbook';
import { useParentOffice } from '~/hooks';
import { useAppSelector } from '~/hooks/useAppSelector';
import { useFillViewportHeight } from '~/hooks/useFillViewportHeight';
import useLoading from '~/hooks/useLoading';
import { useUserSelector } from '~/hooks/useUserSelector';
import { SortFields } from '../enums';

interface IProps {
  filters: IFilter;
}
interface IFilter {
  transactionDate: string;
}

export const useAllTransactionReceiptV2 = ({ filters }: IProps) => {
  const dispatch = useDispatch();
  // Bound the scroll area's height to the viewport so it overflows-and-scrolls
  // internally; otherwise `onEndReached` (infinite scroll) never fires (GOTU-4127).
  const { setRef: setScrollAreaRef, containerRef, maxHeight } = useFillViewportHeight();
  const user = useUserSelector();

  const isDSOStructureEnabled = useRef(shouldUseDsoStructure()).current;

  const offices = useAppSelector((state) => state.user.offices?.map((office) => office.id));
  const childOffices = useAppSelector((state) =>
    state.user.childOffices?.map((office) => office.id),
  );
  const { data, metadata } = useAppSelector((state) => state.receipt.receipts);

  const { isParentOffice } = useParentOffice();

  const officeIds = useMemo(() => {
    if (!isDSOStructureEnabled && !isParentOffice) {
      return [user?.id];
    }

    if (!isDSOStructureEnabled) {
      return [...childOffices];
    }

    return offices;
  }, [childOffices, isDSOStructureEnabled, isParentOffice, offices, user?.id]);

  const [sort, setSort] = useState({
    field: SortFields.TransactionDate,
    order: 'desc',
  });

  const { isLoading } = useLoading([FETCH_RECEIPTS_V2]);
  // Synchronous in-flight lock. `metadata.page` only advances once a request
  // resolves, and the redux loading flag lags a render behind the dispatch, so
  // without this the burst of scroll events fired while pinned at the bottom
  // re-requests the same page many times over (GOTU-4127).
  const isFetchingNextPageRef = useRef(false);

  useEffect(() => {
    // Release the lock once the request settles (success or error).
    if (!isLoading) {
      isFetchingNextPageRef.current = false;
    }
  }, [isLoading]);

  const onFetchNextPage = () => {
    const { isFinal, page } = metadata;

    if (isFinal || isLoading || isFetchingNextPageRef.current) {
      return;
    }

    isFetchingNextPageRef.current = true;
    dispatch(
      fetchReceiptsV2({
        limit: 25,
        officeId: officeIds,
        page: page + 1,
        sortBy: sort.field,
        sortOrder: sort.order,
        transactionDate: filters.transactionDate,
      }),
    );
  };

  const onSortChange = (field: SortFields, value?: TSortValue) => {
    setSort({
      field: (value?.field as SortFields) || SortFields.TransactionDate,
      order: (value?.direction?.toLowerCase() as 'asc' | 'desc') || 'desc',
    });

    containerRef.current?.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    dispatch(
      fetchReceiptsV2({
        limit: 25,
        officeId: officeIds,
        page: 1,
        sortBy: sort.field,
        sortOrder: sort.order,
        transactionDate: filters.transactionDate,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, sort, filters.transactionDate]);

  return {
    receipts: data,
    setScrollAreaRef,
    scrollAreaMaxHeight: maxHeight,
    sort,
    onFetchNextPage,
    onSortChange,
  };
};
